import React from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import {
  GROUP_SHELL_PATH_TO_ADD_GROUP_SHELL,
  BASIC_DATA_FOR_ADDING_GROUP_SHELLS,
} from "@FillInTableContainer/shared/constants/tableCreatorGroupShell.constants";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useCheckDisabledDelete } from "@FillInTableContainer/shared/hooks/useCheckDisabledDelete";
import { useUpdateEntryData } from "@hooks/useUpdateArticleData";
import { useEntryDataState } from "@stores/entryData";
import { useInitEntryDataValue } from "@stores/initEntryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  clickAddRightScopeGroupShellButton: (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
    },
  ) => void;
  handleClickToDeleteLicenseeTableGroupShell: () => void;
  handleKeyDownToDeleteLicenseeTableGroupShell: (event: KeyboardEvent) => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: DealLaScSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useHandleDealTermsLicenseeTableGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const initEntryData = useInitEntryDataValue(getPressIdFromURL());
  const updateEntryData = useUpdateEntryData();
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { checkDisabledDeleteGroupShell } = useCheckDisabledDelete();

  const handleAddGroupShell = (groupShellInfo: {
    tableIndexParameter: number;
    groupShellIndexParameter: number;
    groupShellKeyParameter: string | null;
  }) => {
    const targetGroupShellKeys = ["Main Financial Terms"];
    if (focusStatus.container !== "noFocus") return;

    const {
      tableIndexParameter,
      groupShellIndexParameter,
      groupShellKeyParameter,
    } = groupShellInfo;

    if (groupShellKeyParameter === null) return;

    const targetShell = GROUP_SHELL_PATH_TO_ADD_GROUP_SHELL[
      groupShellKeyParameter
    ](entryData, tableIndexParameter, groupShellIndexParameter);

    if (targetShell === undefined) return;

    const newArrayLength = targetShell.length + 1 || 1;
    const emptyArray = new Array(newArrayLength).fill({});

    const addRightsScopeGroupShellData = emptyArray.map((_, index) => {
      const RightScopeGroupShellData =
        BASIC_DATA_FOR_ADDING_GROUP_SHELLS[articleSection][
          groupShellKeyParameter
        ](initEntryData);

      if (!targetGroupShellKeys.includes(groupShellKeyParameter)) return;

      if (index === groupShellIndexParameter) {
        return RightScopeGroupShellData;
      }

      if (index > groupShellIndexParameter) {
        return targetShell[index - 1];
      }

      if (index < groupShellIndexParameter) {
        return targetShell[index];
      }

      return targetShell[index];
    });

    const changeGroupShellData: Record<string, ArticleEntryData> = {
      "Main Financial Terms": {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": addRightsScopeGroupShellData,
        },
      } as ArticleEntryData,
    };

    setEntryData(changeGroupShellData[groupShellKeyParameter]);
    updateEntryData(changeGroupShellData[groupShellKeyParameter]);
    changeCurrentHistory({
      entryData: changeGroupShellData[groupShellKeyParameter],
      changedDataInfo: {
        target: "groupShell",
        action: "add",
        position: {
          groupShellKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupTableKey,
          groupShellIndex: groupShellIndexParameter,
          subGroupShellIndex: null,
          subGroupShellKey: null,
          shellKey: null,
          shellIndex: null,
          valueIndex: null,
          value: null,
        },
      },
    });

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
  };

  const clickAddRightScopeGroupShellButton = (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
    },
  ) => {
    event.stopPropagation();
    handleAddGroupShell(groupShellInfo);
  };

  const handleClearGroupShell = () => {
    const clearFinancialTermsGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;
      if (info.groupShellKey !== "Main Financial Terms") return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Disclosed Payment Sum(LA&SC)": {
                    ...tableInfo["Disclosed Payment Sum(LA&SC)"],
                    value: [],
                  },
                  "Milestone Payment And Detail": [
                    {
                      ...tableInfo["Milestone Payment And Detail"]?.[0],
                      "Milestone Payment Detail(LA&SC)": {
                        ...tableInfo["Milestone Payment And Detail"]?.[0][
                          "Milestone Payment Detail(LA&SC)"
                        ],
                        value: [],
                      },
                      "Milestone Payment(LA&SC)": {
                        ...tableInfo["Milestone Payment And Detail"]?.[0][
                          "Milestone Payment(LA&SC)"
                        ],
                        value: [],
                      },
                    },
                  ],
                  "Other Payment And Detail": [
                    {
                      ...tableInfo["Other Payment And Detail"]?.[0],
                      "Other Payment Detail(LA&SC)": {
                        ...tableInfo["Other Payment And Detail"]?.[0][
                          "Other Payment Detail(LA&SC)"
                        ],
                        value: [],
                      },
                      "Other Payment(LA&SC)": {
                        ...tableInfo["Other Payment And Detail"]?.[0][
                          "Other Payment(LA&SC)"
                        ],
                        value: [],
                      },
                    },
                  ],
                  "Paying Partner Axis(LA&SC)": {
                    ...tableInfo["Paying Partner Axis(LA&SC)"],
                    value: [],
                  },
                  "Receiving Partner Axis(LA&SC)": {
                    ...tableInfo["Receiving Partner Axis(LA&SC)"],
                    value: [],
                  },
                  "Royalty(LA&SC)": [
                    {
                      ...tableInfo["Royalty(LA&SC)"]?.[0],
                      "Royalty Rate": {
                        ...tableInfo["Royalty(LA&SC)"]?.[0]["Royalty Rate"],
                        value: [],
                      },
                      "Royalty Detail": {
                        ...tableInfo["Royalty(LA&SC)"]?.[0]["Royalty Detail"],
                        value: [],
                      },
                    },
                  ],
                  "Total Payment(LA&SC)": {
                    ...tableInfo["Total Payment(LA&SC)"],
                    value: [],
                  },
                  "Upfront Payment(LA&SC)": {
                    ...tableInfo["Upfront Payment(LA&SC)"],
                    value: [],
                  },
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const clearAdvisorGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;
      if (info.groupShellKey !== "Advisor") return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Advisor Table": entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
            "Advisor Table"
          ]?.map((subGroupShellInfo) => {
            return {
              ...subGroupShellInfo,
              "Advisor Name": {
                ...subGroupShellInfo["Advisor Name"],
                value: [],
              },
              "Advisor Role": {
                ...subGroupShellInfo["Advisor Role"],
                value: [],
              },
              "Party Axis": {
                ...subGroupShellInfo["Party Axis"],
                value: [],
              },
            };
          }),
        },
      };
    };

    const clearOtherFinancialTermsGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;
      if (info.groupShellKey !== "Other Financial Terms") return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Other Financial Terms": {
            ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Other Financial Terms"
            ],
            options:
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
                "Other Financial Terms"
              ]?.options ?? [],
            value: [],
          },
        },
      };
    };

    if (info.groupShellKey === "Main Financial Terms") {
      setEntryData(clearFinancialTermsGroupShell());
      changeCurrentHistory({
        entryData: clearFinancialTermsGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      const financialTermsTableLength =
        entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
          "Main Financial Terms Table"
        ]?.length || 0;

      if (financialTermsTableLength > 1) {
        changeFocusStatus({ type: "delete", container: "groupShell" });
      } else {
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
      }
    }

    if (info.groupShellKey === "Advisor") {
      setEntryData(clearAdvisorGroupShell());
      changeCurrentHistory({
        entryData: clearAdvisorGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      changeValidatedInfo(DEFAULT_VALIDATED_INFO);
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }

    if (info.groupShellKey === "Other Financial Terms") {
      setEntryData(clearOtherFinancialTermsGroupShell());
      changeCurrentHistory({
        entryData: clearOtherFinancialTermsGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      changeValidatedInfo(DEFAULT_VALIDATED_INFO);
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }
  };

  const handleDeleteGroupShell = () => {
    if (checkDisabledDeleteGroupShell()) return;

    const deleteRightScopeGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.filter(
            (_, groupShellInfoIndex) => {
              return groupShellInfoIndex !== info.groupShellIndex;
            },
          ),
        },
      };
    };
    setEntryData(deleteRightScopeGroupShell());
    changeCurrentHistory({
      entryData: deleteRightScopeGroupShell(),
      changedDataInfo: {
        target: "groupShell",
        action: "delete",
        position: {
          groupShellKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupTableKey,
          groupShellIndex,
          subGroupShellIndex: null,
          subGroupShellKey: null,
          shellKey: null,
          shellIndex: null,
          valueIndex: null,
          value: null,
        },
      },
    });

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  };

  const handleClickToDeleteLicenseeTableGroupShell = () => {
    if (focusStatus.type === "clear") {
      handleClearGroupShell();
    }
    if (focusStatus.type === "delete") {
      handleDeleteGroupShell();
    }
  };

  const handleKeyDownToDeleteLicenseeTableGroupShell = (
    event: KeyboardEvent,
  ) => {
    if (focusStatus.type === "clear" && event.key === "Delete") {
      handleClearGroupShell();
    }
    if (focusStatus.type === "delete" && event.key === "Delete") {
      handleDeleteGroupShell();
    }
  };

  return {
    clickAddRightScopeGroupShellButton,
    handleClickToDeleteLicenseeTableGroupShell,
    handleKeyDownToDeleteLicenseeTableGroupShell,
  };
};
